"use strict";

Object.defineProperty(exports, "__esModule", {
  value: true
});
exports.wrapStringsInText = exports.unpackFragment = void 0;
var _react = _interopRequireDefault(require("react"));
var _Text = _interopRequireDefault(require("react-native-web/dist/cjs/exports/Text"));
var _A11yText = _interopRequireDefault(require("../A11yText"));
var _jsxRuntime = require("react/jsx-runtime");
function _interopRequireDefault(e) { return e && e.__esModule ? e : { default: e }; }
/**
 * Unpacks top-level fragments, so that common compositional patterns such as the following examples
 * can be iterated as flat siblings (as if they were `<Child1 /><Child2 /><Child3 />`):
 *
 * - Setting `children` as a property wrapped in a fragment:
 *
 * ```jsx
 * args.children = <><Child1 /><Child2 /><Child3 /></>
 * ```
 *
 * - Defining `children` as a variable wrapped in a fragment:
 *
 * ```jsx
 * const content = <><Child1 /><Child2 /><Child3 /></>
 * if (someCondition) return <SomeWrapper>{content}</SomeWrapper>
 * ```
 *
 * - Using fragments at the top level of a JSX block for conditional rendering:
 *
 * ```jsx
 * <Child1 />
 * {someCondition && (
 *   <>
 *     <Child2 />
 *     <Child3 />
 *   </>
 * )}
 * ```
 *
 * @param {ReactChildren} child
 * @returns {ReactChildren}
 */
const unpackFragment = child => {
  // If this level is a set of top-level siblings rather than one child, check each in turn
  if (_react.default.Children.count(child) > 1) return _react.default.Children.map(child, unpackFragment);

  // When a fragment is found, unpack its children to the top level and check them
  if (child?.type === _react.default.Fragment) return unpackFragment(child.props?.children);

  // Stop unpacking as soon as any non-fragment child is found
  return child;
};
exports.unpackFragment = unpackFragment;
const isStringOrNumber = child => typeof child === 'string' || typeof child === 'number';
// Wrap an A11yText with neighouring text strings so it doesn't split them into multiple <Text>s
const isWrapable = child => isStringOrNumber(child) || child.type === _A11yText.default || child.type?.name === 'FootnoteLink';
const combineKeys = childrenArray => childrenArray.reduce((newKey, child) => `${newKey}${child.key || ''}`, '');

// Group wrappable children for one `<Text>` parent, merging adjacent text nodes
const wrapChild = (child, wrappedText) => {
  const lastIndex = wrappedText.length - 1;
  // If possible, simplify content by combining text nodes into one string.
  // jest-native's `.toHaveTextContent` is buggy about array of text nodes in <Text> elements.
  if (lastIndex >= 0 && isStringOrNumber(child) && isStringOrNumber(wrappedText[lastIndex])) {
    /* eslint-disable-next-line no-param-reassign */
    wrappedText[lastIndex] = `${wrappedText[lastIndex]}${child}`;
  } else {
    wrappedText.push(child);
  }
};

/**
 * React Native on Native crashes if text content is rendered outside `<Text>`, and on web,
 * text style inheritance behaves differently with text compared to regular HTML.
 *
 * Call this function on children that may contain text (strings or numbers) at the top level,
 * and any that are found will be wrapped in a React Native `<Text>` element with supplied props.
 *
 * Note that this does not wrap strings that are nested children of rendered top level children:
 * `wrapStringsInText(<View>Some text</View>)` will not wrap the inner text and will still crash,
 * but `wrapStringsInText(<>{someString}{anotherString}</>)` wraps the strings inside the fragment.
 *
 * @param {ReactChildren} children
 * @param {TextProps} props
 * @returns {ReactChildren}
 */
const wrapStringsInText = function (children) {
  let textProps = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : {};
  const childrenArray = unpackFragment(_react.default.Children.toArray(children));

  // Group adjacent wrapable children together in one Text element to create as few Text elements
  // as possible, e.g. give <X>Text {someString}</X> one Text, same as <X>{`Text ${someString}`}</X>
  const wrapables = [[]];
  let wrapablesIndex = 0;
  childrenArray.forEach(child => {
    if (isWrapable(child)) {
      // Make this child a child of the current `Text`
      wrapChild(child, wrapables[wrapablesIndex]);
    } else {
      // Close current `Text` and start a new one after this child
      wrapables.push(child, []);
      wrapablesIndex += 2;
    }
  });
  const items = wrapables.reduce((flatChildren, group, index) => {
    // Skip nullish children and empty arrays
    if (!group || Array.isArray(group) && !group.length) return flatChildren;
    return [...flatChildren, Array.isArray(group) && group.some(child => isStringOrNumber(child)) ?
    /*#__PURE__*/
    // Wrap text nodes and their wrappable neighbours in Text with as stable a key as possible.
    // Avoid one-item arrays because jest-native's `.toHaveTextContent` is buggy
    // and sometimes fails to match text content in arrays.
    (0, _jsxRuntime.jsx)(_Text.default, {
      ...textProps,
      children: group.length === 1 ? group[0] : group
    }, combineKeys(group) || index) : group];
  }, []);
  return items.length === 1 ? items[0] : items;
};
exports.wrapStringsInText = wrapStringsInText;